import { CMS_BASE_URL } from './constants'
import { buildStrapiRequestHeaders, buildStrapiRequestUrl } from './strapiRequest'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type {
  ResourcesComparisonCardCmsContent,
  ResourcesIconTextItemCmsContent,
  ResourcesPageCmsContent,
  ResourcesPartnerCardCmsContent,
  ResourcesPlanTierCmsContent,
  ResourcesTieredPlanCmsContent,
  ResourcesTitleBodyCardCmsContent,
} from './interfaces'

interface FetchResourcesContentOptions {
  signal?: AbortSignal
  previewToken?: string
}

const RESOURCES_PAGE_ENDPOINT = `/api/resources-page?${
  [
    'populate[technicalOverviewBackgroundDesktop]=true',
    'populate[technicalOverviewBackgroundMobile]=true',
    'populate[technicalOverviewPoints][populate][icon][fields][0]=url',
    'populate[gpuAccelerationCards]=true',
    'populate[technologicalFirstCards][populate]=*',
    'populate[documentationBackgroundDesktop]=true',
    'populate[documentationBackgroundMobile]=true',
    'populate[documentationLinks]=true',
    'populate[tieredPlans][populate][icon][fields][0]=url',
    'populate[tieredPlans][populate][essential][populate][items]=true',
    'populate[tieredPlans][populate][premium][populate][items]=true',
    'populate[partnerProgramPartners][populate][icon][fields][0]=url',
    'populate[partnerProgramBenefits][populate][icon][fields][0]=url',
    'populate[contactButton]=true',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const mapIconTextItems = (value: unknown): ResourcesIconTextItemCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((item): ResourcesIconTextItemCmsContent[] => {
      const iconUrl = readMediaUrl(item.icon)
      const text = readString(item.text)

      if (iconUrl === null || text === null) {
        return []
      }

      return [{ iconUrl, text }]
    })
)

const mapTitleBodyCards = (value: unknown): ResourcesTitleBodyCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): ResourcesTitleBodyCardCmsContent[] => {
      const title = readString(card.title)
      const body = readString(card.body)

      if (title === null || body === null) {
        return []
      }

      return [{ title, body }]
    })
)

const mapComparisonCards = (value: unknown): ResourcesComparisonCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): ResourcesComparisonCardCmsContent[] => {
      const title = readString(card.title)
      const items = toStrapiEntityCollection(card.items)
        .map((item) => readString(item.text))
        .filter((item): item is string => item !== null)

      if (title === null || items.length === 0) {
        return []
      }

      return [{ title, items }]
    })
)

const mapPlanTier = (value: unknown): ResourcesPlanTierCmsContent | null => {
  const tier = toStrapiEntity(value)
  const title = readString(tier?.title)
  const items = toStrapiEntityCollection(tier?.items)
    .map((item) => readString(item.text))
    .filter((item): item is string => item !== null)

  if (title === null || items.length === 0) {
    return null
  }

  return { title, items }
}

const mapTieredPlans = (value: unknown): ResourcesTieredPlanCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((plan): ResourcesTieredPlanCmsContent[] => {
      const iconUrl = readMediaUrl(plan.icon)
      const label = readString(plan.label)
      const essential = mapPlanTier(plan.essential)
      const premium = mapPlanTier(plan.premium)

      if (iconUrl === null || label === null || essential === null || premium === null) {
        return []
      }

      return [{ iconUrl, label, essential, premium }]
    })
)

const mapPartnerCards = (value: unknown): ResourcesPartnerCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((partner): ResourcesPartnerCardCmsContent[] => {
      const iconUrl = readMediaUrl(partner.icon)
      const title = readString(partner.title)
      const description = readString(partner.description)

      if (iconUrl === null || title === null || description === null) {
        return []
      }

      return [{ iconUrl, title, description }]
    })
)

const mapResourcesPageContent = (payload: unknown): ResourcesPageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)

  const technicalOverviewTitle = readString(contentRoot.technicalOverviewTitle)
  const technicalOverviewBody = readString(contentRoot.technicalOverviewBody)
  const technicalOverviewDesktopBackgroundUrl = readMediaUrl(contentRoot.technicalOverviewBackgroundDesktop)
  const technicalOverviewMobileBackgroundUrl = readMediaUrl(contentRoot.technicalOverviewBackgroundMobile)
  const technicalOverviewPoints = mapIconTextItems(contentRoot.technicalOverviewPoints)

  const gpuAccelerationTitle = readString(contentRoot.gpuAccelerationTitle)
  const gpuAccelerationBody = readString(contentRoot.gpuAccelerationBody)
  const gpuAccelerationCards = mapTitleBodyCards(contentRoot.gpuAccelerationCards)

  const technologicalFirstTitle = readString(contentRoot.technologicalFirstTitle)
  const technologicalFirstCards = mapComparisonCards(contentRoot.technologicalFirstCards)

  const documentationTitle = readString(contentRoot.documentationTitle)
  const documentationBody = readString(contentRoot.documentationBody)
  const documentationDesktopBackgroundUrl = readMediaUrl(contentRoot.documentationBackgroundDesktop)
  const documentationMobileBackgroundUrl = readMediaUrl(contentRoot.documentationBackgroundMobile)
  const documentationLinks = toStrapiEntityCollection(contentRoot.documentationLinks)
    .flatMap((link) => {
      const label = readString(link.label)
      const url = readString(link.url)

      if (label === null || url === null) {
        return []
      }

      return [{ label, url }]
    })
  const documentationDownloadButtonText = readString(contentRoot.documentationDownloadButtonText)
  const documentationDownloadButtonUrl = readString(contentRoot.documentationDownloadButtonUrl)

  const tieredPlansTitle = readString(contentRoot.tieredPlansTitle)
  const tieredPlans = mapTieredPlans(contentRoot.tieredPlans)

  const partnerProgramTitle = readString(contentRoot.partnerProgramTitle)
  const partnerProgramDescription = readString(contentRoot.partnerProgramDescription)
  const partnerProgramPartners = mapPartnerCards(contentRoot.partnerProgramPartners)
  const partnerProgramBenefitsTitle = readString(contentRoot.partnerProgramBenefitsTitle)
  const partnerProgramBenefits = mapIconTextItems(contentRoot.partnerProgramBenefits)

  const contactButton = toStrapiEntity(contentRoot.contactButton)
  const contactText = readString(contentRoot.contactText)
  const contactButtonText = readString(contactButton?.label)
  const contactButtonUrl = readString(contactButton?.url)

  if (
    heroTitleText === null ||
    heroDescription === null ||
    technicalOverviewTitle === null ||
    technicalOverviewBody === null ||
    technicalOverviewDesktopBackgroundUrl === null ||
    technicalOverviewMobileBackgroundUrl === null ||
    technicalOverviewPoints.length !== 2 ||
    gpuAccelerationTitle === null ||
    gpuAccelerationBody === null ||
    gpuAccelerationCards.length !== 3 ||
    technologicalFirstTitle === null ||
    technologicalFirstCards.length !== 2 ||
    documentationTitle === null ||
    documentationBody === null ||
    documentationDesktopBackgroundUrl === null ||
    documentationMobileBackgroundUrl === null ||
    documentationLinks.length === 0 ||
    documentationDownloadButtonText === null ||
    documentationDownloadButtonUrl === null ||
    tieredPlansTitle === null ||
    tieredPlans.length !== 2 ||
    partnerProgramTitle === null ||
    partnerProgramDescription === null ||
    partnerProgramPartners.length !== 4 ||
    partnerProgramBenefitsTitle === null ||
    partnerProgramBenefits.length !== 3 ||
    contactText === null ||
    contactButtonText === null ||
    contactButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      titleText: heroTitleText,
      description: heroDescription,
    },
    technicalOverview: {
      title: technicalOverviewTitle,
      body: technicalOverviewBody,
      desktopBackgroundUrl: technicalOverviewDesktopBackgroundUrl,
      mobileBackgroundUrl: technicalOverviewMobileBackgroundUrl,
      points: technicalOverviewPoints,
    },
    gpuAcceleration: {
      title: gpuAccelerationTitle,
      body: gpuAccelerationBody,
      cards: gpuAccelerationCards,
    },
    technologicalFirst: {
      title: technologicalFirstTitle,
      cards: technologicalFirstCards,
    },
    documentation: {
      title: documentationTitle,
      body: documentationBody,
      desktopBackgroundUrl: documentationDesktopBackgroundUrl,
      mobileBackgroundUrl: documentationMobileBackgroundUrl,
      links: documentationLinks,
      downloadButtonText: documentationDownloadButtonText,
      downloadButtonUrl: documentationDownloadButtonUrl,
    },
    tieredPlans: {
      title: tieredPlansTitle,
      plans: tieredPlans,
    },
    partnerProgram: {
      title: partnerProgramTitle,
      description: partnerProgramDescription,
      partners: partnerProgramPartners,
      benefitsTitle: partnerProgramBenefitsTitle,
      benefits: partnerProgramBenefits,
    },
    contact: {
      text: contactText,
      buttonText: contactButtonText,
      buttonUrl: contactButtonUrl,
    },
  }
}

export const fetchResourcesContentFromStrapi = async (
  options: FetchResourcesContentOptions = {},
): Promise<ResourcesPageCmsContent | null> => {
  const response = await fetch(buildStrapiRequestUrl(RESOURCES_PAGE_ENDPOINT, options.previewToken), {
    method: 'GET',
    signal: options.signal,
    headers: buildStrapiRequestHeaders(options.previewToken),
  })

  if (!response.ok) {
    throw new Error(`Unable to fetch resources CMS content (${response.status})`)
  }

  const payload = await response.json() as unknown

  return mapResourcesPageContent(payload)
}
